import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export interface FilePickerOptions {
  accept?: string | string[];
  multiple?: boolean;
  maxFiles?: number;
  allowedMimeTypes?: string[];
  allowedExtensions?: string[];
}

export interface FilePickerResult {
  files: File[];
  rejectedFiles: File[];
}

@Injectable({
  providedIn: 'root',
})
export class FilePickerService {
  private readonly document = inject(DOCUMENT);

  selectFiles(options: FilePickerOptions = {}): Observable<FilePickerResult> {
    return new Observable<FilePickerResult>((subscriber) => {
      const input = this.document.createElement('input');
      input.type = 'file';
      input.style.display = 'none';
      input.accept = this.getAcceptValue(options);
      input.multiple = options.multiple ?? false;

      const emitResult = (result: FilePickerResult): void => {
        subscriber.next(result);
        subscriber.complete();
      };

      const handleChange = (): void => {
        emitResult(this.validateFiles(Array.from(input.files ?? []), options));
      };

      const handleCancel = (): void => {
        emitResult({ files: [], rejectedFiles: [] });
      };

      input.addEventListener('change', handleChange, { once: true });
      input.addEventListener('cancel', handleCancel, { once: true });
      this.document.body.appendChild(input);
      input.click();

      return () => {
        input.removeEventListener('change', handleChange);
        input.removeEventListener('cancel', handleCancel);
        input.remove();
      };
    });
  }

  selectSingleFile(options: Omit<FilePickerOptions, 'multiple' | 'maxFiles'> = {}): Observable<File | null> {
    return new Observable<File | null>((subscriber) => {
      const subscription = this.selectFiles({
        ...options,
        multiple: false,
        maxFiles: 1,
      }).subscribe((result) => {
        subscriber.next(result.files[0] ?? null);
        subscriber.complete();
      });

      return () => subscription.unsubscribe();
    });
  }

  selectSinglePdf(): Observable<File | null> {
    return this.selectSingleFile({
      accept: ['application/pdf', '.pdf'],
      allowedMimeTypes: ['application/pdf'],
      allowedExtensions: ['.pdf'],
    });
  }

  private validateFiles(files: File[], options: FilePickerOptions): FilePickerResult {
    const maxFiles = options.multiple ? options.maxFiles : 1;
    const acceptedFiles: File[] = [];
    const rejectedFiles: File[] = [];

    files.forEach((file) => {
      if (maxFiles && acceptedFiles.length >= maxFiles) {
        rejectedFiles.push(file);
        return;
      }

      if (!this.isAllowedFile(file, options)) {
        rejectedFiles.push(file);
        return;
      }

      acceptedFiles.push(file);
    });

    return { files: acceptedFiles, rejectedFiles };
  }

  private isAllowedFile(file: File, options: FilePickerOptions): boolean {
    const allowedMimeTypes = options.allowedMimeTypes ?? [];
    const allowedExtensions = this.normalizeExtensions(options.allowedExtensions ?? []);

    if (allowedMimeTypes.length === 0 && allowedExtensions.length === 0) {
      return true;
    }

    const hasAllowedMimeType = !!file.type && allowedMimeTypes.includes(file.type);
    const fileName = file.name.toLowerCase();
    const hasAllowedExtension = allowedExtensions.some((extension) => fileName.endsWith(extension));

    return hasAllowedMimeType || hasAllowedExtension;
  }

  private getAcceptValue(options: FilePickerOptions): string {
    if (Array.isArray(options.accept)) {
      return options.accept.join(',');
    }

    if (options.accept) {
      return options.accept;
    }

    return [
      ...(options.allowedMimeTypes ?? []),
      ...this.normalizeExtensions(options.allowedExtensions ?? []),
    ].join(',');
  }

  private normalizeExtensions(extensions: string[]): string[] {
    return extensions.map((extension) => {
      const normalizedExtension = extension.trim().toLowerCase();
      return normalizedExtension.startsWith('.') ? normalizedExtension : `.${normalizedExtension}`;
    });
  }
}
